import { IconButton } from '@components/core/icon-button.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { Skeleton } from '@components/ui/skeleton';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  type MeiaSwimlaneMosTree,
  useCreateMeiaMoeEntry,
  useCreateMeiaSwimlaneMosEntry,
  useDeleteMeiaMoeEntry,
  useDeleteMeiaMosEntry,
  useGetMeiaMoeTemplates,
  useGetMeiaMosTemplates,
  useGetMeiaSwimlaneMosTrees,
  useUpdateMeiaMoeEntry,
  useUpdateMeiaMosEntry,
} from '@queries/meia.query';
import {
  type MeiaSwimlaneMeasuresFormValues,
  type MeiaSwimlaneMosEntryFormValues,
  meiaSwimlaneMeasuresSchema,
} from '@schemas/meia.schema';
import { Trash2Icon } from 'lucide-react';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { MEIA_AUTOFILL_IGNORE_PROPS } from './meia-form-autofill.util';
import { buildEmptyMoe, buildEmptyMos, generateMeiaLocalId, moeFromServer, persistMoeTree } from './meia-measures.util';
import { MeiaMoeCardGroup } from './meia-moe-card.component';
import { MeiaTemplatePopover } from './meia-template-popover.component';

const mosEntryFromTree = (tree: MeiaSwimlaneMosTree): MeiaSwimlaneMosEntryFormValues => ({
  _localId: generateMeiaLocalId(),
  description: tree.mos.description,
  id: tree.mos.id,
  moeEntries: tree.moes.map((moe) => moeFromServer(moe)),
  mosTemplateCode: null,
  removedMoeIds: [],
});

const buildValuesFromTrees = (trees: MeiaSwimlaneMosTree[]): MeiaSwimlaneMeasuresFormValues => ({
  mosEntries: trees.map((tree) => mosEntryFromTree(tree)),
  removedMosIds: [],
});

type MeiaSwimlaneMeasuresFormChildren = (params: {
  /** Reads the form's current dirty state — for host close-guards. */
  getIsDirty: () => boolean;
  isLoading: boolean;
  isSaving: boolean;
  measuresBody: ReactNode;
  /** Persists the step; rejects if a save mutation fails. */
  submit: () => Promise<void>;
  /** Surfaces this step's validation errors without saving; returns step validity. */
  validate: () => boolean;
}) => ReactNode;

type MeiaSwimlaneMeasuresFormProps = {
  canEdit: boolean;
  children: MeiaSwimlaneMeasuresFormChildren;
  missionId: string;
  /**
   * Suppress this form's own success toast so a multi-step host can show a single
   * toast for the whole save. Defaults to false.
   */
  silentSuccess?: boolean;
  swimlaneId: string;
};

/**
 * Inline measures editor for a Stage: a single form holding every Measure of Success
 * (each with its nested Measures of Effectiveness) so the whole step persists through one
 * Save in the host modal footer. The render-prop hands back the body, submit, and pending
 * flag; the host owns the footer.
 */
export const MeiaSwimlaneMeasuresForm: FC<MeiaSwimlaneMeasuresFormProps> = (props) => {
  const { canEdit, children, missionId, silentSuccess = false, swimlaneId } = props;
  const { isLoading, trees } = useGetMeiaSwimlaneMosTrees({ swimlaneId });
  const [isSaving, setIsSaving] = useState(false);
  const [erroredMoeLocalIds, setErroredMoeLocalIds] = useState<ReadonlySet<string>>(new Set());

  const { data: mosTemplates } = useGetMeiaMosTemplates();
  const { data: moeTemplates } = useGetMeiaMoeTemplates();
  const resolvedMosTemplates = mosTemplates?.mosTemplates ?? [];
  const resolvedMoeTemplates = moeTemplates?.moeTemplates ?? [];

  const { mutateAsync: createSwimlaneMos } = useCreateMeiaSwimlaneMosEntry();
  const { mutateAsync: updateMos } = useUpdateMeiaMosEntry();
  const { mutateAsync: deleteMos } = useDeleteMeiaMosEntry();
  const { mutateAsync: createMoe } = useCreateMeiaMoeEntry();
  const { mutateAsync: updateMoe } = useUpdateMeiaMoeEntry();
  const { mutateAsync: deleteMoe } = useDeleteMeiaMoeEntry();

  // Highlights the offending MoE cards (and optionally toasts) for a failed schema
  // parse; shared by submit-time invalidation and the navigation pre-check so both
  // surface the same errors. Returns whether the measures step is currently valid.
  const surfaceMeasureErrors = (value: MeiaSwimlaneMeasuresFormValues, options?: { toast?: boolean }): boolean => {
    const validation = meiaSwimlaneMeasuresSchema.safeParse(value);
    if (validation.success) {
      setErroredMoeLocalIds(new Set());
      return true;
    }

    const erroredLocalIds = new Set<string>();
    let weightMessage: string | undefined;
    for (const issue of validation.error.issues) {
      if (issue.path[0] !== 'mosEntries' || typeof issue.path[1] !== 'number') continue;
      if (issue.path[2] !== 'moeEntries' || typeof issue.path[3] !== 'number') continue;
      const moe = value.mosEntries[issue.path[1]]?.moeEntries[issue.path[3]];
      if (moe) erroredLocalIds.add(moe._localId);
      if (issue.path[4] === 'weight') weightMessage = issue.message;
    }
    setErroredMoeLocalIds(erroredLocalIds);
    if (options?.toast ?? true) {
      errorToast({ description: weightMessage ?? 'Please complete the required fields on every card.' });
    }
    return false;
  };

  const form = useAppForm({
    defaultValues: buildValuesFromTrees(trees),
    onSubmit: async ({ value }) => {
      setErroredMoeLocalIds(new Set());
      setIsSaving(true);
      // Tracks which MoS the save loop is on so a mid-loop failure names the offending entry.
      let failingMosDescription: string | undefined;
      try {
        for (const [mosIndex, mosEntry] of value.mosEntries.entries()) {
          failingMosDescription = mosEntry.description.trim() || 'a new Measure of Success';
          const mosId = mosEntry.id
            ? (await updateMos({ description: mosEntry.description, missionId, mosId: mosEntry.id })).id
            : (await createSwimlaneMos({ description: mosEntry.description, swimlaneId })).id;
          // Write created ids and consumed removals back into the form immediately so a
          // failed pass leaves the form retryable without duplicating earlier work.
          if (!mosEntry.id) {
            form.setFieldValue(`mosEntries[${mosIndex}].id`, mosId);
          }

          await persistMoeTree({
            createMoe,
            deleteMoe,
            moeEntries: mosEntry.moeEntries,
            mosId,
            onMoeCreated: ({ created, moeIndex }) => {
              form.setFieldValue(`mosEntries[${mosIndex}].moeEntries[${moeIndex}].id`, created.id);
            },
            removedMoeIds: mosEntry.removedMoeIds,
            updateMoe,
          });
          if (mosEntry.removedMoeIds.length > 0) {
            form.setFieldValue(`mosEntries[${mosIndex}].removedMoeIds`, []);
          }
        }

        for (const removedMosId of value.removedMosIds) {
          await deleteMos({ missionId, mosId: removedMosId });
        }
        if (value.removedMosIds.length > 0) {
          form.setFieldValue('removedMosIds', []);
        }

        // Rebase the baseline so a clean save doesn't leave the host close-guard dirty —
        // a description-only save changes no ids, so the signature rehydrate below skips it.
        form.reset(form.state.values);
        if (!silentSuccess) successToast({ description: 'Measures saved.' });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to save measures.';
        errorToast({
          description: failingMosDescription ? `Could not save "${failingMosDescription}": ${message}` : message,
        });
        // Re-throw so multi-step hosts awaiting submit() keep the modal open on failure.
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    onSubmitInvalid: ({ value }) => {
      surfaceMeasureErrors(value);
    },
    validators: { onSubmit: meiaSwimlaneMeasuresSchema },
  });

  const treesSignature = useMemo(
    () => trees.map((tree) => `${tree.mos.id}#${tree.moes.map((moe) => moe.id).join(',')}`).join('|'),
    [trees],
  );

  // Rehydrate only when the server-side MoS/MoE set changes (initial load + external structural
  // changes), and never while a save is in flight or the form holds unsaved edits — the save loop's
  // own invalidations change the signature mid-flight, and resetting then would wipe the user's
  // remaining input exactly when a mid-loop failure needs it preserved.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the structural signature + isSaving, not `trees`/`form`
  useEffect(() => {
    if (isSaving || form.state.isDirty) return;
    form.reset(buildValuesFromTrees(trees));
    setErroredMoeLocalIds(new Set());
  }, [isSaving, treesSignature]);

  const submit = (): Promise<void> => form.handleSubmit();

  const validate = (): boolean => surfaceMeasureErrors(form.state.values, { toast: false });

  const renderMosCard = (
    mosEntry: MeiaSwimlaneMosEntryFormValues,
    mosIndex: number,
    onRemoveMos: () => void,
  ): JSX.Element => (
    <div
      className="flex flex-col gap-3 rounded-md border border-gray-200 p-3"
      key={mosEntry._localId}
    >
      <div className="flex items-center justify-between gap-2">
        <BlueLabel
          className="text-xs"
          text="Measure of Success"
        />
        {canEdit && (
          <div className="flex items-center gap-1.5">
            <MeiaTemplatePopover
              onSelect={(template) => {
                form.setFieldValue(`mosEntries[${mosIndex}].description`, template.description ?? template.name);
                form.setFieldValue(`mosEntries[${mosIndex}].mosTemplateCode`, template.id);
              }}
              templates={resolvedMosTemplates.map((template) => ({
                description: template.description,
                id: template.id,
                name: template.name,
              }))}
            />
            <IconButton
              ariaLabel="Delete measure of success"
              icon={<Trash2Icon />}
              onClick={onRemoveMos}
              variant="destructive"
            />
          </div>
        )}
      </div>

      <form.AppField name={`mosEntries[${mosIndex}].description`}>
        {(field) => (
          <field.TextAreaField
            disabled={!canEdit}
            label="Description"
            placeholder="Suppress integrated air defenses inside the first island chain — what does winning look like?"
            required
            rows={3}
          />
        )}
      </form.AppField>

      <form.AppField
        mode="array"
        name={`mosEntries[${mosIndex}].moeEntries`}
      >
        {(moeArrayField) => {
          const moeEntries = moeArrayField.state.value ?? [];
          return (
            <div className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold text-gray-900">Measures of Effectiveness ({moeEntries.length})</h3>

              {moeEntries.map((entry, moeIdx) => (
                <MeiaMoeCardGroup
                  canEdit={canEdit}
                  fields={`mosEntries[${mosIndex}].moeEntries[${moeIdx}]`}
                  form={form}
                  hasError={erroredMoeLocalIds.has(entry._localId)}
                  key={entry._localId}
                  moeOrdinal={moeIdx + 1}
                  moeTemplates={resolvedMoeTemplates.map((template) => ({
                    description: template.description,
                    id: template.id,
                    name: template.name,
                  }))}
                  onRemove={() => {
                    if (entry.id) {
                      form.setFieldValue(`mosEntries[${mosIndex}].removedMoeIds`, (current) => [
                        ...current,
                        entry.id ?? '',
                      ]);
                    }
                    moeArrayField.removeValue(moeIdx);
                  }}
                />
              ))}

              {canEdit && (
                <Button
                  className="self-start"
                  onClick={() => moeArrayField.pushValue(buildEmptyMoe())}
                  type="button"
                  variant="link"
                >
                  + Add Measure of Effectiveness
                </Button>
              )}
            </div>
          );
        }}
      </form.AppField>
    </div>
  );

  const measuresBody = isLoading ? (
    <Skeleton className="h-32 w-full" />
  ) : (
    <form
      {...MEIA_AUTOFILL_IGNORE_PROPS}
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        event.stopPropagation();
        submit();
      }}
    >
      <form.AppForm>
        <form.AppField
          mode="array"
          name="mosEntries"
        >
          {(mosArrayField) => {
            const mosEntries = mosArrayField.state.value ?? [];
            return (
              <div className="flex flex-col gap-3">
                {mosEntries.length === 0 && !canEdit && (
                  <p className="text-xs text-gray-400">No Measures of Success yet.</p>
                )}

                {mosEntries.map((mosEntry, mosIdx) =>
                  renderMosCard(mosEntry, mosIdx, () => {
                    if (mosEntry.id) {
                      form.setFieldValue('removedMosIds', (current) => [...current, mosEntry.id ?? '']);
                    }
                    mosArrayField.removeValue(mosIdx);
                  }),
                )}

                {canEdit && (
                  <Button
                    className="self-start"
                    onClick={() => mosArrayField.pushValue(buildEmptyMos())}
                    size="sm"
                    type="button"
                    variant="link"
                  >
                    + Add Measure of Success
                  </Button>
                )}
              </div>
            );
          }}
        </form.AppField>
      </form.AppForm>
    </form>
  );

  return <>{children({ getIsDirty: () => form.state.isDirty, isLoading, isSaving, measuresBody, submit, validate })}</>;
};
